// dailyCache.ts

import localforage from "localforage";

export const weevilTrakStore = localforage.createInstance({
    driver: [localforage.INDEXEDDB],
    name: "WeevilTrak_database",
    storeName: "WeevilTrak_table"
});

/**
 * ===============================
 * 1️⃣ 计算当前 UTC 业务日期
 * 刷新时间：UTC 02:15
 * ===============================
 */
function getCurrentBusinessDate(): string {
    const now = new Date();

    const refreshHour = 2;
    const refreshMinute = 15;

    const utcYear = now.getUTCFullYear();
    const utcMonth = now.getUTCMonth();
    const utcDate = now.getUTCDate();
    const utcHours = now.getUTCHours();
    const utcMinutes = now.getUTCMinutes();

    const businessDate = new Date(Date.UTC(utcYear, utcMonth, utcDate));

    // 如果当前 UTC 时间小于 02:15，则属于前一天
    if (
        utcHours < refreshHour ||
    (utcHours === refreshHour && utcMinutes < refreshMinute)
    ) {
        businessDate.setUTCDate(businessDate.getUTCDate() - 1);
    }

    return businessDate.toISOString().slice(0, 10); // YYYY-MM-DD
}

/**
 * ===============================
 * 2️⃣ 构建版本化缓存 Key
 * 格式: daily:2026-03-01:userList
 * ===============================
 */
function buildCacheKey(baseKey: string, businessDate: string) {
    return `daily:${businessDate}:${baseKey}`;
}

/**
 * ===============================
 * 3️⃣ 并发锁
 * ===============================
 */
const pendingRequests = new Map<string, Promise<unknown>>();

/**
 * ===============================
 * 4️⃣ 自动清理旧缓存
 * 默认保留最近 keepDays 天（按“业务日”计算，而非自然日）
 * ===============================
 */
function parseYyyyMmDdToUtcDate(yyyyMmDd: string): Date {
    const [y, m, d] = yyyyMmDd.split("-").map(Number);
    return new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1));
}

async function cleanOldCache(baseKey: string, keepDays: number, businessDate: string) {
    const keys = await weevilTrakStore.keys();

    const anchor = parseYyyyMmDdToUtcDate(businessDate); // 业务日 00:00:00Z
    const daysToKeep = Math.max(1, keepDays);

    const validDates = new Set<string>();
    for (let i = 0; i < daysToKeep; i++) {
        const date = new Date(anchor);
        date.setUTCDate(date.getUTCDate() - i);
        validDates.add(date.toISOString().slice(0, 10));
    }

    for (const key of keys) {
        if (!key.startsWith("daily:")) continue;
        if (!key.includes(`:${baseKey}`)) continue;

        const parts = key.split(":");
        const datePart = parts[1];

        // 不在允许保留的“业务日集合”内的一律清掉
        if (!validDates.has(datePart)) {
            await weevilTrakStore.removeItem(key);
        }
    }
}

/**
 * ===============================
 * 5️⃣ 主缓存函数
 * ===============================
 */

interface FetchOptions {
  forceRefresh?: boolean
  keepDays?: number
}

export async function fetchWithDailyCache<T>(
    baseKey: string,
    fetcher: () => Promise<T>,
    options?: FetchOptions
): Promise<T> {
    const businessDate = getCurrentBusinessDate();
    const key = buildCacheKey(baseKey, businessDate);

    const { forceRefresh = false, keepDays = 1 } = options || {};

    // 如果已有相同请求正在进行，直接返回同一个 Promise
    const existingRequest = pendingRequests.get(key);
    if (existingRequest) {
        return existingRequest as Promise<T>;
    }

    const requestPromise = (async () => {
        try {
            if (!forceRefresh) {
                const cached = await weevilTrakStore.getItem<T>(key);
                if (cached !== null) {
                    return cached;
                }
            }

            // 请求新数据
            const freshData = await fetcher();
            await weevilTrakStore.setItem(key, freshData);

            // 异步清理旧缓存（不阻塞当前流程）
            cleanOldCache(baseKey, keepDays, businessDate).catch(console.error);

            return freshData;
        } finally {
            pendingRequests.delete(key);
        }
    })();

    pendingRequests.set(key, requestPromise);

    return requestPromise;
}

/**
 * ===============================
 * 6️⃣ 手动清空某个缓存
 * ===============================
 */
export async function clearDailyCache(baseKey: string) {
    const keys = await weevilTrakStore.keys();

    for (const key of keys) {
        if (key.startsWith("daily:") && key.includes(`:${baseKey}`)) {
            await weevilTrakStore.removeItem(key);
        }
    }
}